const STORAGE_KEY = 'compliance-menu-open';

// sessionStorage può lanciare (modalità privata, storage bloccato): in quel caso il menu funziona senza persistenza
const readOpenIds = () => {
    try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
        return [];
    }
};

const writeOpenIds = ids => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (e) {}
};

const FIXED_MENU_KEY = 'compliance-menu-fixed';

// Preferenza menu ad altezza fissa, salvata nel browser tra una visita e l'altra. Predefinito: attivo
const readFixedMenu = () => {
    try {
        return localStorage.getItem(FIXED_MENU_KEY) !== '0';
    } catch (e) {
        return true;
    }
};

const writeFixedMenu = fixed => {
    try {
        localStorage.setItem(FIXED_MENU_KEY, fixed ? '1' : '0');
    } catch (e) {}
};

const LOADING_CLASS = 'menu-loading';
const NO_TRANSITION_CLASS = 'menu-no-transition';
const DESKTOP_QUERY = '(min-width: 1200px)';

// Letto ogni volta e non messo in cache: il menu è nel DOM solo dopo il caricamento della pagina
const getMenu = () => document.getElementById('compliance-menu');

/*
 * Porta il link attivo nell'area visibile del menu, solo da desktop (da 1200px in su).
 * Si scorre il menu e non la pagina, quindi niente scrollIntoView.
 */
export const scrollMenuToActive = () => {
    if (!window.matchMedia(DESKTOP_QUERY).matches) return;

    const menu = getMenu();
    const active = menu?.querySelector('a.active');
    if (!active) return;

    const menuRect = menu.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const offset = activeRect.top - menuRect.top + menu.scrollTop;

    menu.scrollTo({ top: offset - (menu.clientHeight - active.offsetHeight) / 20 });
};

/*
 * Nasconde il menu (classe menu-loading) al click su un link, durante il cambio pagina.
 * Non si applica ai <details>: aprirli non deve far sparire il menu.
 */
const hideMenuOnLinkClick = menu => {
    menu.addEventListener('click', e => {
        const link = e.target.closest('a[href]');
        if (!link || e.defaultPrevented || e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.target === '_blank') return;
        menu.classList.add(LOADING_CLASS);
    });

    // Tornando indietro la pagina può arrivare dalla cache con il menu ancora nascosto
    window.addEventListener('pageshow', e => {
        if (e.persisted) menu.classList.remove(LOADING_CLASS);
    });
};

/*
 * Ricorda nella sessione i <details> aperti a mano (data-id = ID pagina) e li riapre al caricamento.
 * I rami della pagina corrente arrivano già aperti dal PHP.
 * La riapertura avviene senza animazione: altrimenti i rami crescono dopo scrollMenuToActive()
 * e spingono in basso la voce attiva (nei browser senza scroll anchoring, es. Safari).
 * La classe si toglie al frame successivo, quando lo scroll è già stato calcolato.
 */
const persistOpenState = menu => {
    const openIds = readOpenIds();
    menu.classList.add(NO_TRANSITION_CLASS);
    menu.querySelectorAll('details[data-id]').forEach(details => {
        if (openIds.includes(Number(details.dataset.id))) details.open = true;
    });
    requestAnimationFrame(() => menu.classList.remove(NO_TRANSITION_CLASS));

    // toggle non fa bubbling: serve il listener in fase di capture
    menu.addEventListener('toggle', e => {
        const id = Number(e.target.dataset?.id);
        if (!id) return;
        const ids = readOpenIds().filter(openId => openId !== id);
        if (e.target.open) ids.push(id);
        writeOpenIds(ids);
    }, true);
};

const REVEAL_MAX = 150; // px di voci figlie da mostrare al massimo
const REVEAL_DURATION = 450; // ms, come l'animazione di apertura dei <details> (_common.scss)

/*
 * Aprendo a mano un <details> vicino al fondo del menu ad altezza fissa le figlie finirebbero fuori vista:
 * solo in quel caso il menu scorre in basso quanto basta per farle intravedere, senza far uscire in alto il <summary>.
 * Lo scroll accompagna l'animazione di apertura: a ogni frame si limita allo scroll massimo del momento,
 * perché il contenuto sta ancora crescendo.
 */
const revealOnOpen = menu => {
    menu.addEventListener('click', e => {
        const summary = e.target.closest('summary');
        if (!summary || !window.matchMedia(DESKTOP_QUERY).matches || !menu.closest('.is-menu-fixed')) return;

        // L'apertura avviene dopo il click: si controlla al frame successivo
        requestAnimationFrame(() => {
            const details = summary.parentElement;
            const children = details.querySelector(':scope > [data-children]');
            if (!details.open || !children) return;

            const menuRect = menu.getBoundingClientRect();
            const summaryRect = summary.getBoundingClientRect();
            const margin = (menu.clientHeight - summary.offsetHeight) / 20;
            // Altezza finale delle figlie: il contenitore ha già la sua altezza, è ::details-content a ritagliarlo
            const revealBottom = summaryRect.bottom + Math.min(children.offsetHeight, REVEAL_MAX);
            const needed = revealBottom - (menuRect.bottom - margin);
            if (needed <= 0) return;

            const start = menu.scrollTop;
            const maxShift = summaryRect.top - menuRect.top - margin;
            const target = start + Math.min(needed, maxShift);
            const startTime = performance.now();

            const step = now => {
                const t = Math.min(1, (now - startTime) / REVEAL_DURATION);
                const eased = 1 - (1 - t) ** 3;
                const max = menu.scrollHeight - menu.clientHeight;
                menu.scrollTop = Math.min(max, start + (target - start) * eased);
                if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        });
    });
};

const HOVER_SCROLL_SPEED = 400; // px al secondo

/*
 * Scorre il menu finché il mouse resta sopra il pulsante (direction: 1 in basso, -1 in alto).
 * Solo mouse: al tocco non c'è un hover da interrompere, quindi il click scorre di una "pagina" del menu.
 */
const scrollOnHover = (menu, button, direction) => {
    if (!button) return;

    let frame = null;
    let lastTime = 0;
    // Posizione tenuta a parte: scrollTop può essere arrotondato e con incrementi piccoli lo scroll si bloccherebbe
    let position = 0;

    const step = now => {
        const max = menu.scrollHeight - menu.clientHeight;
        position = Math.min(max, Math.max(0, position + direction * HOVER_SCROLL_SPEED * (now - lastTime) / 2500));
        lastTime = now;
        menu.scrollTop = position;

        const reachedEnd = direction > 0 ? position >= max : position <= 0;
        frame = reachedEnd ? null : requestAnimationFrame(step);
    };

    const stop = () => {
        cancelAnimationFrame(frame);
        frame = null;
    };

    button.addEventListener('pointerenter', e => {
        if (e.pointerType !== 'mouse' || frame) return;
        position = menu.scrollTop;
        frame = requestAnimationFrame(now => {
            lastTime = now;
            step(now);
        });
    });

    button.addEventListener('pointerleave', stop);

    button.addEventListener('click', () => {
        if (frame) return;
        menu.scrollBy({ top: direction * menu.clientHeight * .8, behavior: 'smooth' });
    });
};

/*
 * Attiva la sfumatura (via data-fade-top / data-fade-bottom) solo se il menu ha altro contenuto da scorrere
 * sopra o sotto. Si aggiorna allo scroll e quando cambia l'altezza del menu o del suo contenuto.
 */
export const initMenu = () => {
    const menu = getMenu();
    if (!menu) return;

    persistOpenState(menu);
    hideMenuOnLinkClick(menu);
    revealOnOpen(menu);
    scrollOnHover(menu, document.getElementById('compliance-menu-down-scroll'), 1);
    scrollOnHover(menu, document.getElementById('compliance-menu-up-scroll'), -1);

    const update = () => {
        const max = menu.scrollHeight - menu.clientHeight;
        menu.toggleAttribute('data-fade-top', menu.scrollTop > 1);
        menu.toggleAttribute('data-fade-bottom', max - menu.scrollTop > 1);
    };

    menu.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(menu);
    if (menu.firstElementChild) observer.observe(menu.firstElementChild);
    update();
};

/*
 * Componente Alpine della griglia compliance: apertura del menu su mobile e toggle menu ad altezza fissa
 * (sticky, con pulsanti di scorrimento) / altezza libera. Il toggle agisce sulla classe is-menu-fixed.
 */
export const complianceMenuGrid = () => ({
    open: false,
    desktop: window.matchMedia(DESKTOP_QUERY).matches,
    fixedMenu: readFixedMenu(),

    init() {
        window.matchMedia(DESKTOP_QUERY).addEventListener('change', e => this.desktop = e.matches);
        this.$nextTick(() => {
            initMenu();
            scrollMenuToActive();
        });
    },

    toggleFixedMenu() {
        this.fixedMenu = !this.fixedMenu;
        writeFixedMenu(this.fixedMenu);
        // Tornando all'altezza fissa il menu riparte da scrollTop 0: si riporta in vista il link attivo
        if (this.fixedMenu) this.$nextTick(scrollMenuToActive);
    }
});
